import { useCallback, useEffect, useMemo, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import {
  BrokerInvoiceDocument,
  BrokerInvoicesResponse,
} from "../../../types/brokers";
import UploadInvoiceModal from "./UploadInvoiceModal";

const BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ??
  "http://localhost:8000";

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtAmount = (n: number): string =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);

const fmtPeriod = (year: number | null, month: number | null): string => {
  if (year === null || month === null) return "—";
  return `${String(month).padStart(2, "0")}/${year}`;
};

export default function BrokerInvoicesTab({
  brokerInternalId,
  token,
}: {
  brokerInternalId: number;
  token: string | null;
}) {
  const confirm = useConfirm();
  const [data, setData] = useState<BrokerInvoicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<string>("");
  const [monthFilter, setMonthFilter] = useState<string>("");
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (yearFilter) params.set("year", yearFilter);
    if (monthFilter) params.set("month", monthFilter);
    try {
      const response = await request<BrokerInvoicesResponse>(
        `/api/admin/operations/brokers/${brokerInternalId}/invoices?${params.toString()}`,
        { token },
      );
      setData(response);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [brokerInternalId, token, yearFilter, monthFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async (invoice: BrokerInvoiceDocument) => {
    // We can't just window.open because the endpoint requires a Bearer token —
    // we fetch the binary manually and trigger the download via a blob URL.
    setBusyId(invoice.invoiceDocumentId);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/brokers/invoices/${invoice.invoiceDocumentId}/download`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) {
        throw new HttpError(response.status, await response.text());
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = invoice.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo descargar",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (invoice: BrokerInvoiceDocument) => {
    const ok = await confirm({
      title: "Eliminar factura",
      message: `Se eliminará la factura "${invoice.name}" del broker. Esta acción no se puede deshacer. ¿Continuar?`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    setBusyId(invoice.invoiceDocumentId);
    setError(null);
    try {
      await request(
        `/api/admin/operations/brokers/invoices/${invoice.invoiceDocumentId}`,
        { method: "DELETE", token },
      );
      await load();
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo eliminar",
      );
    } finally {
      setBusyId(null);
    }
  };

  const years = useMemo(() => {
    if (!data) return [];
    const set = new Set<number>();
    data.items.forEach((i) => {
      if (i.year !== null) set.add(i.year);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [data]);

  if (loading && !data) {
    return <div style={{ color: "#73879C" }}>Cargando facturas…</div>;
  }

  return (
    <div>
      {error && (
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.5rem 0.7rem",
            borderRadius: "0.4rem",
            marginBottom: "0.8rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
          marginBottom: "0.7rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: ".82rem", color: "#73879C" }}>
          <strong style={{ color: "#1f2933" }}>{data?.total ?? 0}</strong> facturas
          {data && data.amountTotal > 0 && (
            <>
              {" "}
              · <strong style={{ color: "#1f2933" }}>
                {fmtAmount(data.amountTotal)}
              </strong>
              {" "}
              total
            </>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">Todos los años</option>
          {years.map((y) => (
            <option key={y} value={String(y)}>
              {y}
            </option>
          ))}
        </select>
        <select
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
          style={selectStyle}
        >
          <option value="">Todos los meses</option>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={String(m)}>
              {String(m).padStart(2, "0")}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          style={primaryBtnStyle}
        >
          + Subir factura
        </button>
      </div>

      <div
        style={{
          background: "white",
          borderRadius: "0.5rem",
          border: "1px solid #e9ecef",
          overflow: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: ".82rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>Período</Th>
              <Th>Archivo</Th>
              <Th>Importe</Th>
              <Th>Subida</Th>
              <Th>Mimetype</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {data && data.items.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: "1.2rem",
                    textAlign: "center",
                    color: "#73879C",
                  }}
                >
                  Sin facturas.
                </td>
              </tr>
            )}
            {data?.items.map((invoice) => (
              <tr
                key={invoice.invoiceDocumentId}
                style={{ borderTop: "1px solid #f1f3f5" }}
              >
                <Td>
                  <strong>{fmtPeriod(invoice.year, invoice.month)}</strong>
                </Td>
                <Td>
                  <span
                    style={{ fontFamily: "monospace", fontSize: ".78rem" }}
                    title={invoice.invoiceDocumentId}
                  >
                    {invoice.name}
                  </span>
                </Td>
                <Td>
                  <strong>{fmtAmount(invoice.amount)}</strong>
                </Td>
                <Td>{fmtDateTime(invoice.createdAt)}</Td>
                <Td>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: ".7rem",
                      color: "#73879C",
                    }}
                  >
                    {invoice.mimetype || "—"}
                  </span>
                </Td>
                <Td>
                  <div style={{ display: "flex", gap: "0.3rem" }}>
                    <ActionButton
                      tone="primary"
                      onClick={() => handleDownload(invoice)}
                      disabled={busyId !== null}
                      loading={busyId === invoice.invoiceDocumentId}
                    >
                      Descargar
                    </ActionButton>
                    <ActionButton
                      tone="danger"
                      onClick={() => handleDelete(invoice)}
                      disabled={busyId !== null}
                      loading={busyId === invoice.invoiceDocumentId}
                    >
                      Eliminar
                    </ActionButton>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {uploadOpen && (
        <UploadInvoiceModal
          brokerInternalId={brokerInternalId}
          token={token}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "0.55rem 0.65rem",
        textAlign: "left",
        fontWeight: 600,
        color: "#1f2933",
        fontSize: ".68rem",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: "0.55rem 0.65rem" }}>{children}</td>;
}

function ActionButton({
  onClick,
  tone,
  loading,
  disabled,
  children,
}: {
  onClick: () => void;
  tone: "primary" | "danger";
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const palette =
    tone === "danger"
      ? { bg: "#fde2e2", fg: "#b32424", border: "#f5c6cb" }
      : { bg: "#0069d9", fg: "white", border: "#0069d9" };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "0.25rem 0.55rem",
        borderRadius: "0.35rem",
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontSize: ".72rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.6 : 1,
      }}
    >
      {loading ? "…" : children}
    </button>
  );
}

const selectStyle: React.CSSProperties = {
  padding: "0.35rem 0.5rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".78rem",
  color: "#1f2933",
};

const primaryBtnStyle: React.CSSProperties = {
  padding: "0.4rem 0.8rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".78rem",
  fontWeight: 600,
  cursor: "pointer",
};
